/**
 * AnalyticsInitializer - Initializes Google Analytics for the application
 * This component should be placed near the root of your app for proper tracking
 *
 * Features:
 * - Loads Google Analytics script dynamically
 * - Tracks page views on route changes
 * - Provides analytics context to the rest of the app
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAnalytics } from '../hooks/useAnalytics';
import { shouldEnableTracking } from '../lib/tracking';

export default function AnalyticsInitializer({ children }) {
  const location = useLocation();
  const { trackPageView } = useAnalytics();
  const trackingEnabled = shouldEnableTracking();

  if (!trackingEnabled) {
    return children;
  }

  /**
   * Track page view when route changes (SPA navigation)
   * GA4 will also auto-track page loads, but we need manual tracking for SPA
   */
  useEffect(() => {
    // Get page title from document or route
    const pageTitle = document.title || 'Illumine Studio';

    // Track the page view with path and title
    trackPageView(location.pathname, pageTitle);

    // Scroll to top for better UX
    window.scrollTo(0, 0);
  }, [location, trackPageView]);

  /**
   * Track performance metrics (Core Web Vitals)
   * This helps monitor page load performance for SEO
   */
  useEffect(() => {
    // Only run in browser with Navigation Timing API support
    if (!window.performance || !window.performance.getEntriesByType) return;

    // Wait for page load to complete
    const onPageLoadComplete = () => {
      try {
        // Get navigation timing data
        const navTiming = window.performance.getEntriesByType('navigation')[0];
        if (navTiming) {
          const metrics = {
            // DNS lookup time
            dns_time: Math.round(navTiming.domainLookupEnd - navTiming.domainLookupStart),
            // TCP connection time
            connection_time: Math.round(navTiming.connectEnd - navTiming.connectStart),
            // Time to First Byte
            ttfb: Math.round(navTiming.responseStart - navTiming.navigationStart),
            // DOM content loaded time
            dom_interactive: Math.round(navTiming.domInteractive - navTiming.navigationStart),
            // Full page load time
            page_load_time: Math.round(navTiming.loadEventEnd - navTiming.navigationStart),
          };

          if (window.gtag) {
            window.gtag('event', 'page_performance', metrics);
          }
        }
      } catch (error) {
        console.error('Error tracking performance metrics:', error);
      }
    };

    // Track when page load is complete
    if (document.readyState === 'complete') {
      onPageLoadComplete();
    } else {
      window.addEventListener('load', onPageLoadComplete);
      return () => window.removeEventListener('load', onPageLoadComplete);
    }
  }, []);

  /**
   * Track unhandled errors globally
   */
  useEffect(() => {
    const handleError = (event) => {
      if (window.gtag) {
        window.gtag('event', 'exception', {
          description: `${event.filename}:${event.lineno}:${event.colno} - ${event.message}`,
          fatal: true,
        });
      }
      // You can also log to external error tracking service here
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  return children;
}
